import { useRef, useState } from 'react';

import { useNavigate } from 'react-router';

import { IconArrowLeft } from '@tabler/icons-react';
import { useQuery } from '@tanstack/react-query';
import { useAtomValue } from 'jotai';
import { useTranslation } from 'react-i18next';

import { Button, Card, Typography } from '@cfa/react-components';
import { MessageBlock, Modal } from '@cfa/react-core';

import { isValidPlanId } from '@/utils/isValidPlanId';
import { itemByLanguage } from '@/utils/language';
import { cn } from '@/utils/tailwind';

import QuizPreviewCard from '@/components/Cards/QuizPreviewCard';
import DocumentPreview from '@/components/DocumentPreview/DocumentPreview';
import ScrollModalFooterCore from '@/components/ScrollModal/ScrollModalFooterCore';
import ScrollModalHeaderCore from '@/components/ScrollModal/ScrollModalHeaderCore';

import { routes } from '@/constants/routes';

import { BaseReferenceDTO } from '@/services/content-api-types';

import {
  getCustomQuizDetailsOptions,
  getPlanAndAssignmentsOptions,
  Plan,
} from '@/_generated/api-clients/xp';
import { sourceDataFamily } from '@/atoms/data-view';
import PlanStepContainer from '@/routes/TrainingPlans/BuildPlans/EditablePlanItems/PlanStepContainer';
import Section from '@/routes/TrainingPlans/BuildPlans/EditablePlanItems/Section';

interface PreviewTemplateModalProps {
  isOpen: boolean;
  onClose: () => void;
  onCopy: (id: string) => void;
  onTemplateBackButtonClick: () => void;
  plan: Plan;
  onAdd?: (template: Plan) => void;
  isLoading?: boolean;
  onViewProgress?: () => void;
}

const PreviewTemplateModal: React.FC<PreviewTemplateModalProps> = ({
  isOpen,
  onClose,
  onCopy,
  onTemplateBackButtonClick,
  plan,
  onAdd,
  isLoading = false,
  onViewProgress,
}) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const managedPlans = useAtomValue(sourceDataFamily<Plan>('manage-plans'));
  const isAlreadyAdded = managedPlans.some(p => p.id === plan.id);
  const [isResourcePreview, setIsResourcePreview] = useState(false);
  const [previewDocument, setPreviewDocument] = useState<BaseReferenceDTO>({});
  const [previewQuizId, setPreviewQuizId] = useState('');
  const modalBodyRef = useRef<HTMLDivElement>(null);

  const {
    data: templatePlanAndAssignments,
    isSuccess,
    isFetching,
  } = useQuery({
    ...getPlanAndAssignmentsOptions({ path: { planId: plan.id } }),
    enabled: !!plan.id && isValidPlanId(plan.id),
  });

  const {
    data: quizData,
    error: quizError,
    isSuccess: isQuizSuccess,
    isFetching: isQuizFetching,
  } = useQuery({
    ...getCustomQuizDetailsOptions({ path: { customQuizId: previewQuizId } }),
    enabled: !!previewQuizId,
  });

  // keep template for content api preview and no flash in XP (https://cfacorp.atlassian.net/browse/PATHWAY-2063)
  const templatePlan =
    isSuccess && !isFetching ? templatePlanAndAssignments.plan : null;

  const sections = templatePlan?.content?.sections;
  const steps = templatePlan?.content?.steps;

  const planNameEn = itemByLanguage(plan.name, '', 'English');

  // @TODO: Use a better identifier for compliance plans?
  const complianceDescription = (() => {
    if (!planNameEn || plan.category !== 'compliance') return null;

    const name = planNameEn.toLowerCase();
    if (name.includes('food safety')) {
      return t('TrainingPlans.templates.complianceDescriptions.foodSafety');
    }
    if (name.includes('pci') && name.includes('cybersecurity')) {
      return t(
        'TrainingPlans.templates.complianceDescriptions.pciCybersecurity',
      );
    }
    if (name.includes('gift card')) {
      return t('TrainingPlans.templates.complianceDescriptions.giftCard');
    }
    if (name.includes('harassment')) {
      return t('TrainingPlans.templates.complianceDescriptions.harassment');
    }
    return null;
  })();

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      onClose();
    }
  };

  const modalTitle = (() => {
    if (isResourcePreview) {
      return previewDocument.name;
    }
    if (previewQuizId) {
      return itemByLanguage(quizData?.name);
    }
    return itemByLanguage(plan.name);
  })();

  const onBackButtonClick = () => {
    setIsResourcePreview(false);
    setPreviewDocument({});
    setPreviewQuizId('');
  };

  const handleResourceOrQuizPreview = (
    documentOrQuiz: BaseReferenceDTO | string,
  ) => {
    if (typeof documentOrQuiz === 'string') {
      setPreviewQuizId(documentOrQuiz);
    } else {
      setIsResourcePreview(true);
      setPreviewDocument(documentOrQuiz);
    }
  };

  const handleBackButtonClick = () => {
    if (isResourcePreview || previewQuizId) {
      onBackButtonClick();
    } else {
      onTemplateBackButtonClick();
    }
  };

  const renderContent = () => {
    if (isResourcePreview) {
      return (
        <DocumentPreview
          previewDocument={previewDocument}
          handleGoBack={onBackButtonClick}
          isTemplatePreview
        />
      );
    }
    if (previewQuizId) {
      return !isQuizFetching && (isQuizSuccess || quizError) ? (
        <div>
          <QuizPreviewCard quiz={quizData} quizError={quizError} />
        </div>
      ) : null;
    }
    return (
      <div className="mx-auto flex w-full flex-col gap-6 pb-8">
        {steps?.map(step => (
          <Card className="no-hover px-4!" key={step.id}>
            <PlanStepContainer
              className="'no-drag"
              isPreview
              step={step}
              handleResourceOrQuizPreview={handleResourceOrQuizPreview}
            />
          </Card>
        ))}
        {sections?.map((section, index) => (
          <Section
            index={index}
            isPreview={true}
            key={section.id}
            section={section}
            handleResourceOrQuizPreview={handleResourceOrQuizPreview}
            className={'no-hover'}
          />
        ))}
      </div>
    );
  };

  const getButtonText = () => {
    if (plan.readonly) {
      return isAlreadyAdded ? t('Button.added') : t('Button.add');
    }
    return t('Button.copy');
  };

  const handleButtonClick = () => {
    if (plan.id) {
      if (plan.readonly) {
        if (onAdd) {
          onAdd(plan);
        } else {
          navigate(routes.plans.addReadOnlyPlan({ planId: plan.id }));
        }
      } else {
        onCopy(plan.id);
      }
    }
  };

  const showComplianceBanner =
    Boolean(complianceDescription) && !isResourcePreview;
  const showReadonlyBanner =
    plan.readonly && !isResourcePreview && !previewQuizId;

  return (
    <Modal.Overlay
      className="cfa-modal-content z-2000"
      isOpen={isOpen}
      onOpenChange={handleOpenChange}
      isDismissable
    >
      <Modal.Dialog
        size="lg"
        className="relative flex! w-full! flex-col! overflow-y-hidden! lg:lg:max-w-[840px]! [&>section]:flex! [&>section]:h-full! [&>section]:flex-col!"
      >
        <ScrollModalHeaderCore
          scrollRef={modalBodyRef}
          className="flex! justify-center! pb-8!"
        >
          <div className="absolute top-4 -left-6">
            <Button
              className="-translate-2.5! pl-0!"
              color="secondary"
              onClick={handleBackButtonClick}
              size="md"
              variant="text"
            >
              <IconArrowLeft />
            </Button>
          </div>
          <div className="flex w-full items-center justify-center px-4">
            <div className="text-center">
              <Typography variant="overline1" className="mb-2! text-center!">
                {t('TrainingPlans.preview')}
              </Typography>
              {modalTitle}
            </div>
          </div>
        </ScrollModalHeaderCore>
        <div
          ref={modalBodyRef}
          className={cn('min-h-0! flex-auto! overflow-y-auto!', {
            'bg-base-2': isResourcePreview || previewQuizId,
          })}
        >
          <Modal.Content
            className={cn(
              'mx-6! max-w-full! justify-start! gap-5! p-0! [text-align:unset]!',
            )}
          >
            {(showComplianceBanner || showReadonlyBanner) && (
              <div className="flex flex-col gap-4">
                {showComplianceBanner && (
                  <MessageBlock.Root
                    severity="info"
                    className="w-full! text-start"
                  >
                    {complianceDescription}
                  </MessageBlock.Root>
                )}
                {showReadonlyBanner && (
                  <MessageBlock.Root
                    severity="info"
                    className="w-full! text-start"
                  >
                    {t('TrainingPlans.templates.previewInfo.line2')}
                  </MessageBlock.Root>
                )}
              </div>
            )}
            {renderContent()}
          </Modal.Content>
        </div>
        {!isResourcePreview && !previewQuizId && (
          <ScrollModalFooterCore scrollRef={modalBodyRef}>
            <div className="flex! flex-1! justify-center!">
              <Button
                color="secondary"
                data-testid="copyTemplateFromPreviewButton"
                disabled={plan.readonly && isAlreadyAdded}
                loading={isLoading}
                onClick={handleButtonClick}
                variant="filled"
                className="min-w-0!"
              >
                {getButtonText()}
              </Button>
              {plan.readonly && isAlreadyAdded && onViewProgress && (
                <Button
                  color="secondary"
                  data-testid="viewProgressButton"
                  onClick={onViewProgress}
                  variant="text"
                  className="min-w-0!"
                >
                  {t('Button.viewProgress')}
                </Button>
              )}
            </div>
          </ScrollModalFooterCore>
        )}
      </Modal.Dialog>
    </Modal.Overlay>
  );
};

export default PreviewTemplateModal;
